import {getCurrentISOOffset, getCurrentTimeZoneId, Temporal} from "@/shared/utils/datefunctions";

type Endpoint = "start" | "end";

/** The exchanged value: pure data the parent seeds and reads back via `onValueChange`. */
export interface DateTimeRangeValue {
	start: Temporal.ZonedDateTime | null;
	end: Temporal.ZonedDateTime | null;
	breakTime: number;
}

/**
 * The panel's internal working state: the value plus the transient anchor flag. Kept out of
 * {@link DateTimeRangeValue} because it is interaction state, not data the parent needs to own.
 */
export interface DateTimeRangeEditState extends DateTimeRangeValue {
	/**
	 * Whether the duration is the anchor: while set, editing one endpoint moves the *opposite* endpoint to
	 * keep the duration fixed. Starts unset until the user edits the duration.
	 */
	durationChanged: boolean;
}

/**
 * Seeds the working state from an exchanged value. An anchor flag already carried by the value (an echoed
 * edit state) is preserved, so re-seeding from a value the panel emitted via `onValueChange` round-trips
 * losslessly.
 */
export function createEditState(value: DateTimeRangeValue & Partial<Pick<DateTimeRangeEditState, "durationChanged">>): DateTimeRangeEditState {
	return {
		...cloneDateTimeRangeValue(value),
		durationChanged: value.durationChanged ?? false
	};
}

function calculateDuration(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null, breakTimeMinutes: number): number {
	if (!start || !end) {
		return 0;
	}
	return (end.epochMilliseconds - start.epochMilliseconds) - (breakTimeMinutes || 0) * 60000;
}

const opposite = (endpoint: Endpoint): Endpoint => endpoint === "start" ? "end" : "start";

export function cloneDateTimeRangeValue<T extends DateTimeRangeValue>(value: T): T {
	// Temporal.ZonedDateTime is immutable, so a shallow copy is a full clone.
	return {...value};
}

/** The endpoint's offset, falling back to the current zone when the field is empty. */
function endpointTimeZone(endpoint: Temporal.ZonedDateTime | null): string {
	return endpoint?.offset ?? getCurrentISOOffset();
}

/** Whether editing an endpoint should move the opposite one to preserve the current duration. */
function durationIsAnchor(value: DateTimeRangeEditState): boolean {
	return value.durationChanged && value.start !== null && value.end !== null;
}

/**
 * When `localize` is set, a *calculated* endpoint's offset should follow the local date (DST-aware) rather
 * than the persisted source offset — the same refresh `RecordDialog.asLocalDateTime` applies to the
 * recalculated side. Keeps the instant and only swaps in the refreshed offset. The field the user edited
 * directly is never passed through here, so a manually entered timezone is preserved.
 */
export function refreshOffsetToLocalDate(dateTime: Temporal.ZonedDateTime | null, localize: boolean): Temporal.ZonedDateTime | null {
	if (!localize || !dateTime) {
		return dateTime;
	}
	// The local offset for this endpoint's wall-clock date (DST-aware), then keep the instant and pin that offset.
	const localOffset = dateTime.toPlainDateTime().toZonedDateTime(getCurrentTimeZoneId()).offset;
	return dateTime.withTimeZone(localOffset);
}

export function currentDuration(value: DateTimeRangeValue): number {
	return calculateDuration(value.start, value.end, value.breakTime);
}

/**
 * Recalculates the `target` endpoint from its opposite, keeping `duration` fixed (end = start + duration,
 * start = end − duration, both across the break). The result's offset follows the local date when
 * `localizeCalculatedOffset` is set.
 */
function recalculate<T extends DateTimeRangeValue>(value: T, target: Endpoint, localizeCalculatedOffset: boolean, duration = currentDuration(value)): T {
	const source = value[opposite(target)];
	if (!source) {
		return value;
	}
	const sign = target === "end" ? 1 : -1;
	// Keep the instant, then re-express it in the target endpoint's own offset (withTimeZone preserves the instant).
	const computed = source
		.add({milliseconds: sign * duration})
		.add({minutes: sign * value.breakTime})
		.withTimeZone(endpointTimeZone(value[target]));
	// `value` is always a fresh clone from the caller, so mutating the recalculated endpoint in place is safe.
	value[target] = refreshOffsetToLocalDate(computed, localizeCalculatedOffset);
	return value;
}

function withDate(dateTime: Temporal.ZonedDateTime | null, date: Temporal.PlainDate | null): Temporal.ZonedDateTime | null {
	if (!date) {
		return null;
	}
	if (!dateTime) {
		return date.toZonedDateTime(getCurrentISOOffset());
	}
	return dateTime.with({year: date.year, month: date.month, day: date.day});
}

function withTime(dateTime: Temporal.ZonedDateTime | null, time: string | null): Temporal.ZonedDateTime | null {
	if (!time) {
		return dateTime;
	}
	const [hours, minutes, seconds] = time.split(":").map(Number);
	const base = dateTime ?? Temporal.Now.zonedDateTimeISO(getCurrentISOOffset());
	return base.with({hour: hours, minute: minutes, second: seconds || 0, millisecond: 0});
}

function withTimeZone(dateTime: Temporal.ZonedDateTime | null, timeZone: string | null): Temporal.ZonedDateTime | null {
	if (!dateTime || !timeZone || !globals.timeZonePattern.test(timeZone)) {
		return dateTime;
	}
	// Keep the wall-clock time and re-interpret it at the new offset (the instant shifts).
	return dateTime.toPlainDateTime().toZonedDateTime(timeZone);
}

/**
 * Applies a date edit: the opposite endpoint follows when the duration is anchored.
 */
export function applyDateChange(value: DateTimeRangeEditState, endpoint: Endpoint, date: Temporal.PlainDate | null, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	const duration = currentDuration(value);
	const next = cloneDateTimeRangeValue(value);
	next[endpoint] = withDate(next[endpoint], date);
	return durationIsAnchor(value) ? recalculate(next, opposite(endpoint), localizeCalculatedOffset, duration) : next;
}

/**
 * Edits one field (time or timezone) of a single endpoint. Empty/invalid commits keep the previous
 * endpoint so the input snaps back instead of creating a partial date-time state.
 */
function applyEndpointFieldChange(value: DateTimeRangeEditState, endpoint: Endpoint, field: "time" | "timeZone", fieldValue: string | null, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	const duration = currentDuration(value);
	const next = cloneDateTimeRangeValue(value);
	next[endpoint] = field === "time" ? withTime(next[endpoint], fieldValue) : withTimeZone(next[endpoint], fieldValue);
	return durationIsAnchor(value) ? recalculate(next, opposite(endpoint), localizeCalculatedOffset, duration) : next;
}

export function applyTimeZoneChange(value: DateTimeRangeEditState, endpoint: Endpoint, timeZone: string | null, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	return applyEndpointFieldChange(value, endpoint, "timeZone", timeZone, localizeCalculatedOffset);
}

/**
 * Applies a combined date+time edit from a single date-time — date and time follow it together, moving the
 * endpoint onto another calendar day when needed. `null` keeps the previous endpoint.
 */
export function applyDateTimeChange(value: DateTimeRangeEditState, endpoint: Endpoint, dateTime: Temporal.ZonedDateTime | null, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	const duration = currentDuration(value);
	const next = cloneDateTimeRangeValue(value);
	next[endpoint] = dateTime ?? next[endpoint];
	return durationIsAnchor(value) ? recalculate(next, opposite(endpoint), localizeCalculatedOffset, duration) : next;
}

export function applyBreakTimeChange(value: DateTimeRangeEditState, breakTime: number, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	const duration = currentDuration(value);
	const next = {...cloneDateTimeRangeValue(value), breakTime};
	return durationIsAnchor(value) ? recalculate(next, "start", localizeCalculatedOffset, duration) : next;
}

export function applyDurationChange(value: DateTimeRangeEditState, duration: number, localizeCalculatedOffset: boolean): DateTimeRangeEditState {
	const next = recalculate(cloneDateTimeRangeValue(value), "end", localizeCalculatedOffset, duration);
	next.durationChanged = duration !== 0;
	return next;
}

export function shouldShowTimeZoneFields(value: DateTimeRangeValue, forceTimeZoneFields: boolean): boolean {
	const currentTimeZone = getCurrentISOOffset();
	return globals.showTimeZone ||
		endpointTimeZone(value.start) !== endpointTimeZone(value.end) ||
		(forceTimeZoneFields &&
			(endpointTimeZone(value.start) !== currentTimeZone || endpointTimeZone(value.end) !== currentTimeZone));
}
